import type { ColumnDef } from '@tanstack/react-table';
import { ViewAsScreenSchema } from '@voll-intelligence/domain';

import type { AccessEvent, AccessGrant } from '@web/data/repositories';
import { currentLocale, t } from '@web/i18n';
import { DataTable } from '@web/ui/core/molecules';

/** Audit timestamps keep the seconds: the order of reads within a minute matters. */
function formatAt(value: Date): string {
  return value.toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'medium' });
}

function grantEnd(grant: AccessGrant): string {
  return grant.closedAt
    ? t('travelerAccess.grants.closedAt', { at: formatAt(grant.closedAt) })
    : t('travelerAccess.grants.expiresAt', { at: formatAt(grant.expiresAt) });
}

const GRANT_COLUMNS: ColumnDef<AccessGrant>[] = [
  {
    id: 'traveler',
    header: () => t('travelerAccess.col.traveler'),
    cell: ({ row }) => <span className="font-mono">{row.original.travelerPublicId}</span>,
  },
  {
    id: 'operator',
    header: () => t('travelerAccess.col.operator'),
    cell: ({ row }) => <span className="font-mono">{row.original.operatorId}</span>,
  },
  {
    id: 'justification',
    header: () => t('travelerAccess.col.justification'),
    cell: ({ row }) => row.original.justification,
  },
  {
    id: 'card',
    header: () => t('travelerAccess.col.card'),
    cell: ({ row }) => <span className="font-mono">{row.original.cardId ?? '—'}</span>,
  },
  {
    id: 'start',
    header: () => t('travelerAccess.col.start'),
    cell: ({ row }) => formatAt(row.original.createdAt),
  },
  {
    id: 'end',
    header: () => t('travelerAccess.col.end'),
    cell: ({ row }) => grantEnd(row.original),
  },
];

/**
 * Screens translate; reads keep the raw route pattern (`GET /trips/:tripId`)
 * the gate recorded; a search shows its term — the auditor's view is the only
 * place it surfaces, and it never goes into a URL.
 */
function eventOperation(event: AccessEvent): string {
  if (event.kind === 'search') {
    return t('travelerAccess.event.searchTerm', { query: event.params.query ?? '' });
  }
  const screen = ViewAsScreenSchema.safeParse(event.operation);
  if (event.kind === 'screen' && screen.success) {
    return t(`travelerAccess.screen.${screen.data}`);
  }
  return event.operation;
}

function eventDetail(event: AccessEvent): string {
  if (event.kind === 'search') {
    return t('travelerAccess.event.results', { count: Number(event.params.resultCount ?? 0) });
  }
  return Object.entries(event.params)
    .map(([key, value]) => `${key}: ${value}`)
    .join(' · ');
}

const EVENT_COLUMNS: ColumnDef<AccessEvent>[] = [
  {
    id: 'at',
    header: () => t('travelerAccess.col.at'),
    cell: ({ row }) => formatAt(row.original.at),
  },
  {
    id: 'kind',
    header: () => t('travelerAccess.col.kind'),
    cell: ({ row }) => t(`travelerAccess.kind.${row.original.kind}`),
  },
  {
    id: 'operator',
    header: () => t('travelerAccess.col.operator'),
    cell: ({ row }) => <span className="font-mono">{row.original.operatorId}</span>,
  },
  {
    id: 'traveler',
    header: () => t('travelerAccess.col.traveler'),
    cell: ({ row }) => <span className="font-mono">{row.original.travelerPublicId ?? '—'}</span>,
  },
  {
    id: 'operation',
    header: () => t('travelerAccess.col.operation'),
    cell: ({ row }) => eventOperation(row.original),
  },
  {
    id: 'detail',
    header: () => t('travelerAccess.col.detail'),
    cell: ({ row }) => eventDetail(row.original),
  },
];

export function GrantsTable({ grants }: Readonly<{ grants: AccessGrant[] }>): React.JSX.Element {
  return (
    <DataTable
      columns={GRANT_COLUMNS}
      data={grants}
      getRowId={(grant) => grant.grantId}
      emptyMessage={t('travelerAccess.grants.empty')}
    />
  );
}

export function EventsTable({ events }: Readonly<{ events: AccessEvent[] }>): React.JSX.Element {
  return (
    <DataTable
      columns={EVENT_COLUMNS}
      data={events}
      getRowId={(event) => event.eventId}
      emptyMessage={t('travelerAccess.events.empty')}
    />
  );
}
